<template>
    <application-layout>
        <v-container>
            <h1 class="mb-1">Manage Alerts</h1>
            <application-modal v-model="createAlert" @collapse="clearAlert" :submitted="submitted">
                <v-btn
                    data-testid="create-alert-modal-btn"
                    class="create-alert-modal-btn primary mb-4 ml-0"
                    slot="modal-trigger"
                    @click.stop="createAlert = true"
                >
                    <v-icon>add</v-icon>
                    Add New Alert
                </v-btn>
                <div slot="modal-title">Add New Alert</div>

                <template slot="modal-body">
                    <v-form ref="form">
                        <facilities-select :alert="alert" v-model="alert.facility_id" :returnObject="true"/>

                        <v-textarea
                            data-testid="create-alert-textarea"
                            class="create-alert-textarea"
                            filled
                            v-model="alert.text"
                            placeholder="Enter alert..."
                            label="Alert text"
                            :rules="inputRules"
                        />

                        <v-menu
                          ref="createAlertDateMenu"
                          v-model="createAlertDateMenu"
                          :close-on-content-click="false"
                          :return-value.sync="alert.expires_at"
                          transition="scale-transition"
                          offset-y
                          min-width="auto"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="alert.expires_at"
                              label="Select expiration or leave empty to show indefinitely."
                              prepend-icon="mdi-calendar"
                              readonly
                              v-bind="attrs"
                              v-on="on"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="alert.expires_at"
                            no-title
                            scrollable
                            :min="today"
                          >
                            <v-spacer></v-spacer>
                            <v-btn
                              text
                              color="primary"
                              @click="createAlertDateMenu = false"
                            >
                              Cancel
                            </v-btn>
                            <v-btn
                              text
                              color="primary"
                              @click="$refs.createAlertDateMenu.save(alert.expires_at)"
                            >
                              OK
                            </v-btn>
                          </v-date-picker>
                        </v-menu>

                        <v-select
                            data-testid="create-alert-color-menu"
                            class="create-alert-color-menu"
                            filled
                            :items="colorsOptions"
                            name="color"
                            label="Select Alert Color"
                            v-model="alert.selectedColor"
                            item-text="color"
                            item-value="value"
                            return-object
                        />

                        <div class="mt-1 mb-3 px-1 warning--text text-body-2">
                            This alert will display
                            <span v-if="!alert.facility_id.value"> globally</span>
                            <span v-else> for facility {{ alert.facility_id.name }}</span>
                            <span v-if="alert.expires_at"> until {{ formatDate(alert.expires_at) }}</span>
                            <span v-else> indefinitely</span>.
                        </div>
                    </v-form>
                </template>

                <v-btn
                    data-testid="create-alert-btn"
                    class="create-alert-btn"
                    slot="create-button"
                    color="primary"
                    :loading="loading"
                    @click="postAlert"
                    :disabled="!alert.text"
                >
                    Create Alert
                </v-btn>
            </application-modal>
            <v-flex>
                <div class="text-h5 font-weight-bold mt-3 mb-2">Facility Alerts</div>
                <v-data-table
                    :headers="headers"
                    :items="alerts"
                    :loading="loading"
                    no-data-text="There are no alerts to display."
                >
                    <template slot="item" slot-scope="props">
                      <tr>
                        <td>{{ props.item.id }}</td>
                        <td>{{ itemIsGlobal(props.item.facility) }}</td>
                        <td>{{ props.item.facility ? props.item.facility.district : `--` }}</td>
                        <td>{{ props.item.text }}</td>
                        <td>{{ timeStamp(props.item.updated_at) }}</td>
                        <td>{{ formatDate(props.item.expires_at) }}</td>
                        <td>
                            <v-layout>
                                <handle-alert
                                  :alert.sync="props.item"
                                  :colors="colorsOptions"
                                />
                                <v-btn small class="ml-2 error" @click="toggleDelete(props.item)">Delete</v-btn>
                            </v-layout>
                        </td>
                      </tr>
                    </template>
                </v-data-table>
            </v-flex>
        </v-container>
        <application-modal v-model="deleteModal">
            <div slot="modal-title">Delete Alert</div>
            <div slot="modal-body">
                <v-layout column>
                    <v-layout class="mb-1 subheading">
                        <v-flex xs3>Alert ID: </v-flex>
                        <v-flex>{{ selectedAlert.id }}</v-flex>
                    </v-layout>
                    <v-layout class="mb-1 subheading">
                        <v-flex xs3>Facility ID: </v-flex>
                        <v-flex>{{ alertIsGlobal(selectedAlert.facility_id) }}</v-flex>
                    </v-layout>
                    <v-layout class="mb-1 subheading">
                        <v-flex xs3>Note: </v-flex>
                        <v-flex>{{ selectedAlert.text }}</v-flex>
                    </v-layout>
                    <v-layout class="mb-1 subheading">
                        <v-flex xs3>Expires At: </v-flex>
                        <v-flex>{{ formatDate(selectedAlert.expires_at) }}</v-flex>
                    </v-layout>
                    <v-layout class="mb-1 subheading">
                        <v-flex xs3>Last Updated: </v-flex>
                        <v-flex>{{ timeStamp(selectedAlert.updated_at )}}</v-flex>
                    </v-layout>
                </v-layout>
            </div>
            <v-btn
                data-testid="delete-alert-btn"
                class="delete-alert-btn"
                slot="create-button"
                color="primary"
                :loading="loading"
                @click="deleteAlert(selectedAlert.id)"
            >
                Delete
            </v-btn>
        </application-modal>

    </application-layout>
</template>

<script>
import ApplicationLayout from "@/layouts/ApplicationLayout";
import ApplicationModal from "@/components/ApplicationModal";
import FacilitiesSelect from "@/components/FacilitiesSelect";
import HandleAlert from "@/components/HandleAlert";
import { alertsHeaders } from "@/helpers/alerts/alerts-data-table-headers";
import { inputRules } from "@/helpers/form-rules";
import moment from "moment";

export default {
  data() {
    return {
      loading: false,
      createAlert: false,
      submitted: false,
      deleteModal: false,
      colorsOptions: [
        "Blue",
        "Green",
        "Red",
        "Orange"
      ],

      alert: {
        text: "",
        expires_at: null,
        facility_id: "",
        selectedColor: undefined
      },
      selectedAlert: "",

      inputRules: inputRules,

      headers: alertsHeaders,

      createAlertDateMenu: false
    };
  },

  methods: {
    itemIsGlobal(facility) {
      return !facility ? "Global" : facility.location_code;
    },

    alertIsGlobal(id) {
      return !id ? "Global" : id;
    },

    timeStamp(date) {
      return moment.utc(date).local().fromNow();
    },

    formatDate(date) {
      return date ? moment(date).local().add(24, "hours").format("MM-DD-YYYY, hh:mm a") : "Indefinite";
    },

    toggleDelete(alert) {
      this.deleteModal = true;
      this.selectedAlert = alert;
    },

    deleteAlert(id) {
      this.loading = true;
      this.$store.dispatch("deleteAlert", id)
        .then(() => {
          this.$store.dispatch("getAllAlerts");
          this.deleteModal = false;
          this.selectedAlert = "";
          this.loading = false;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.loading = false;
        });
    },

    postAlert() {
      this.loading = true;
      let alert = {
        facility_id: this.alert.facility_id.value,
        text: this.alert.text,
        expires_at: this.alert.expires_at ? moment(this.alert.expires_at).local().add(24, "hours").format("YYYY-MM-DD, hh:mm a") : null,
        color: this.updatedColor
      };
      this.$store.dispatch("submitAlert", alert)
        .then(() => {
          this.$store.dispatch("getAllAlerts");
          this.clearAlert();
          this.loading = false;
          this.createAlert = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    clearAlert() {
      this.alert = {
        text: "",
        expires_at: null,
        facility_id: "",
        selectedColor: undefined
      };
      this.$refs.form.reset();
    }
  },

  computed: {
    alerts() {
      return this.$store.state.alerts.alerts;
    },

    updatedColor() {
      if (this.alert.selectedColor === "Blue") {
        return "info";
      } else if (this.alert.selectedColor === "Green") {
        return "success";
      } else if (this.alert.selectedColor === "Red") {
        return "error";
      } else if (this.alert.selectedColor === "Orange" || this.alert.selectedColor === null) {
        return "warning";
      }
      return "warning";
    },

    today() {
      return new moment().format("YYYY-MM-DD");
    }
  },

  created() {
    this.$store.dispatch("getAllAlerts");
  },

  components: {
    ApplicationLayout,
    ApplicationModal,
    FacilitiesSelect,
    HandleAlert
  }
};
</script>

<style scoped>
.v-data-table::v-deep > .v-data-table__wrapper > table > tbody > tr > td,
.v-data-table::v-deep > .v-data-table__wrapper > table > tbody > tr > th {
  white-space: unset;
}
</style>
